export const M4_UNIT_4 = {
  id: "m4-u4-present-continuous-questions",
  title: "Unidad 4: What's Happening?",
  topic: "Master asking and answering about ongoing actions! Learn to use present continuous questions with tech vocabulary and daily activities. Essential for phone calls, video chats, and describing what you're doing right now!",
  iconType: "smartphone",
  grammarImage: "/grammar/grammar-m4-u4.jpg",
  grammarText: "Present Continuous Questions: What ARE you doing? / IS she working? / WHERE are they going? Estructura: Question word + AM/IS/ARE + subject + verb-ING. Respuestas: I AM working / She IS cooking.",
  
  sets: [
    // SET 1: Basic Questions - What are you doing?
    {
      id: "m4-u4-set1",
      title: "Set 1: What Are You Doing?",
      questions: [
        {
          type: "mc",
          q: "'___ you doing right now?'",
          options: ["What are", "What is", "What do"],
          a: "What are",
          tip: "Questions: What + ARE/IS + subject + -ING? 'What are you doing?' = ¿Qué estás haciendo?"
        },
        {
          type: "input",
          q: "Answer: 'I ___ dinner.' (cook - continuous)",
          a: "am cooking"
        },
        {
          type: "order",
          q: "Ordena: '¿Qué estás haciendo?'",
          words: ["What", "are", "you", "doing?"],
          a: "What are you doing?"
        },
        {
          type: "mc",
          q: "'___ she watching TV?'",
          options: ["Is", "Are", "Does"],
          a: "Is",
          tip: "Yes/No questions: IS/ARE + subject + -ING? 'Is she watching...?'"
        },
        {
          type: "match",
          q: "Match question to answer:",
          pairs: [
            { left: "What are you doing?", right: "I'm cooking dinner" },
            { left: "Is he working?", right: "Yes, he is" },
            { left: "Are they studying?", right: "No, they aren't" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete question: '___ he doing his homework?' (Is/Are/Do)",
          a: "Is"
        },
        {
          type: "mc",
          q: "ERROR: 'What you are doing?' - Corrección:",
          options: ["What are you doing?", "What you doing?", "What do you doing?"],
          a: "What are you doing?",
          tip: "Order matters! Question word + ARE/IS + subject + -ING. 'What ARE you doing?' ✓"
        },
        {
          type: "order",
          q: "Ordena: '¿Está ella limpiando la casa?'",
          words: ["Is", "she", "cleaning", "the", "house?"],
          a: "Is she cleaning the house?"
        },
        {
          type: "input",
          q: "Complete: '___ they eating lunch right now?' (question)",
          a: "Are"
        },
        {
          type: "mc",
          q: "Phone call: 'Hey! ___ you doing? Can you talk?'",
          options: ["What are", "What is", "What"],
          a: "What are",
          successtip: "✓ PHONE INTRO! Universal question! 📱 'What are you doing?' = how every casual call starts in English!"
        }
      ]
    },

    // SET 2: Yes/No Questions
    {
      id: "m4-u4-set2",
      title: "Set 2: Are You...? / Is She...?",
      questions: [
        {
          type: "mc",
          q: "'___ you working from home today?'",
          options: ["Are", "Is", "Do"],
          a: "Are"
        },
        {
          type: "input",
          q: "Short answer: 'Are you watching TV?' - 'Yes, I ___.'",
          a: "am"
        },
        {
          type: "order",
          q: "Ordena: '¿Estás trabajando ahora mismo?'",
          words: ["Are", "you", "working", "right", "now?"],
          a: "Are you working right now?"
        },
        {
          type: "mc",
          q: "'___ your boss calling you?'",
          options: ["Is", "Are", "Does"],
          a: "Is"
        },
        {
          type: "match",
          q: "Match question to short answer:",
          pairs: [
            { left: "Are you busy?", right: "Yes, I am" },
            { left: "Is he sleeping?", right: "No, he isn't" },
            { left: "Are they coming?", right: "Yes, they are" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: '___ she using the computer?' (question)",
          a: "Is"
        },
        {
          type: "mc",
          q: "ERROR: 'You are working?' - ¿Qué está mal?",
          options: [
            "Necesita 'Are' al inicio para ser pregunta",
            "Falta el sujeto",
            "Debe usar 'Do'"
          ],
          a: "Necesita 'Are' al inicio para ser pregunta",
          tip: "Statement = You are working. Question = ARE you working? Verb goes first!"
        },
        {
          type: "order",
          q: "Ordena: '¿Están ellos viendo una película?'",
          words: ["Are", "they", "watching", "a", "movie?"],
          a: "Are they watching a movie?"
        },
        {
          type: "input",
          q: "Negative answer: 'Is he cooking?' - 'No, he ___.'",
          a: "isn't / is not"
        },
        {
          type: "mc",
          q: "Video call: '___ you hear me? ___ my camera working?'",
          options: ["Are, Is", "Do, Is", "Are, Are"],
          a: "Are, Is",
          successtip: "✓ ZOOM LIFE! Remote work! 💻 'Are you' for you-actions, 'Is' for it-things. Essential since 2020!"
        }
      ]
    },

    // SET 3: Where/Why/Who Questions
    {
      id: "m4-u4-set3",
      title: "Set 3: Where & Why Questions",
      questions: [
        {
          type: "mc",
          q: "'___ are you going?'",
          options: ["Where", "What", "Who"],
          a: "Where",
          tip: "Where = dónde (lugar). 'Where are you going?' = ¿A dónde vas?"
        },
        {
          type: "input",
          q: "Complete: '___ is she calling?' (Who/Where/What)",
          a: "Who"
        },
        {
          type: "order",
          q: "Ordena: '¿Por qué estás llorando?'",
          words: ["Why", "are", "you", "crying?"],
          a: "Why are you crying?"
        },
        {
          type: "mc",
          q: "'___ are they waiting for the bus?'",
          options: ["Where", "What", "Who"],
          a: "Where"
        },
        {
          type: "match",
          q: "Match question word to meaning:",
          pairs: [
            { left: "Where", right: "dónde (place)" },
            { left: "Why", right: "por qué (reason)" },
            { left: "Who", right: "quién (person)" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: '___ are you running?' (reason)",
          a: "Why"
        },
        {
          type: "mc",
          q: "'___ is he texting?'",
          options: ["Who", "Where", "Why"],
          a: "Who",
          successtip: "✓ WHO! Person! 📱 'Who are you texting?' = common question when someone's on their phone!"
        },
        {
          type: "order",
          q: "Ordena: '¿Dónde estás trabajando ahora?'",
          words: ["Where", "are", "you", "working", "now?"],
          a: "Where are you working now?"
        },
        {
          type: "input",
          q: "Complete: '___ are you smiling?' (reason)",
          a: "Why"
        },
        {
          type: "mc",
          q: "Full question: '___ is she talking to on the phone?'",
          options: ["Who", "What", "Where"],
          a: "Who"
        }
      ]
    },

    // SET 4: Tech Activities Vocabulary
    {
      id: "m4-u4-set4",
      title: "Set 4: Digital Life",
      questions: [
        {
          type: "mc",
          q: "'I ___ to music on Spotify right now.'",
          options: ["am listening", "listening", "am listen"],
          a: "am listening",
          tip: "ERROR común! Necesitas AM/IS/ARE + verb-ING. 'I listening' ✗, 'I am listening' ✓"
        },
        {
          type: "input",
          q: "Complete: 'She ___ her phone.' (charge)",
          a: "is charging"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy viendo videos en YouTube'",
          words: ["I'm", "watching", "videos", "on", "YouTube."],
          a: "I'm watching videos on YouTube."
        },
        {
          type: "mc",
          q: "'He ___ through Instagram.'",
          options: ["is scrolling", "scrolling", "scroll"],
          a: "is scrolling",
          successtip: "✓ SCROLLING! Modern verb! 📱 Scrolling = deslizar/navegar por feed. Everyone does it!"
        },
        {
          type: "match",
          q: "Match tech activity to -ING form:",
          pairs: [
            { left: "browse", right: "browsing" },
            { left: "stream", right: "streaming" },
            { left: "download", right: "downloading" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'They ___ a movie on Netflix.' (stream)",
          a: "are streaming",
          successtip: "✓ STREAMING! Modern entertainment! 🎬 Stream = ver contenido online. Netflix, Prime, Disney+!"
        },
        {
          type: "mc",
          q: "ERROR: 'I am browse the internet' - Corrección:",
          options: ["I am browsing the internet", "I browsing the internet", "I browse the internet"],
          a: "I am browsing the internet",
          tip: "After AM/IS/ARE, need -ING! 'Am browsing' ✓, 'am browse' ✗"
        },
        {
          type: "order",
          q: "Ordena: 'Ella está descargando una aplicación'",
          words: ["She's", "downloading", "an", "app."],
          a: "She's downloading an app."
        },
        {
          type: "input",
          q: "Complete: 'We ___ our notifications.' (check)",
          a: "are checking"
        },
        {
          type: "mc",
          q: "'What are you doing?' - 'I ___ my emails.'",
          options: ["am checking", "checking", "check"],
          a: "am checking"
        }
      ]
    },

    // SET 5: Mini-Readings - Social Media Reality
    {
      id: "m4-u4-set5",
      title: "Set 5: Social Media Life",
      questions: [
        {
          type: "mc",
          q: "Lee:\n\n'Maria is sitting on the bus. She's scrolling through TikTok and watching funny videos. She's also listening to music with her earbuds. She's not looking at the window - she's looking at her screen the whole time.'\n\n¿Qué está haciendo Maria?",
          options: [
            "Scrolling through TikTok and listening to music",
            "Talking on the phone",
            "Reading a book"
          ],
          a: "Scrolling through TikTok and listening to music"
        },
        {
          type: "input",
          q: "Según el texto, ¿qué NO está haciendo Maria? → She ___ looking at the window.",
          a: "isn't / is not"
        },
        {
          type: "mc",
          q: "Lee:\n\n'Carlos is at work but he's not working right now. He's taking a break and he's checking his Instagram. He's liking photos and he's reading comments. His manager isn't watching him!'\n\n¿Qué está haciendo Carlos durante su break?",
          options: [
            "Checking Instagram and liking photos",
            "Working on his computer",
            "Talking to his manager"
          ],
          a: "Checking Instagram and liking photos"
        },
        {
          type: "match",
          q: "Match the tech term to its meaning:",
          pairs: [
            { left: "Scrolling", right: "Deslizar por el feed" },
            { left: "Liking", right: "Dar 'me gusta'" },
            { left: "Posting", right: "Publicar contenido" }
          ],
          a: "completed",
          successtip: "✓ SOCIAL MEDIA VOCAB! Universal actions! 📱 Like = dar like, post = publicar, scroll = navegar feed!"
        },
        {
          type: "order",
          q: "Traduce: 'Estoy publicando una foto en Instagram'",
          words: ["I'm", "posting", "a", "photo", "on", "Instagram."],
          a: "I'm posting a photo on Instagram."
        },
        {
          type: "mc",
          q: "Lee:\n\n'Ana is having lunch but she's not really eating. She's taking photos of her food for Instagram. She's choosing the best filter and she's writing a caption. Finally, she's posting her story!'\n\n¿Qué está haciendo Ana antes de comer?",
          options: [
            "Taking photos and posting on Instagram",
            "Calling her friends",
            "Reading emails"
          ],
          a: "Taking photos and posting on Instagram",
          successtip: "✓ FOODSTAGRAMMING! Modern reality! 📸 Taking food pics = universal millennial behavior! #FoodPorn"
        },
        {
          type: "input",
          q: "Complete: 'She's choosing a ___ for her photo.' (del texto de Ana)",
          a: "filter"
        },
        {
          type: "order",
          q: "Traduce: '¿Estás viendo historias en Instagram?'",
          words: ["Are", "you", "watching", "stories", "on", "Instagram?"],
          a: "Are you watching stories on Instagram?"
        },
        {
          type: "input",
          q: "Complete: 'He's ___ through his Facebook feed.' (scroll)",
          a: "scrolling"
        },
        {
          type: "mc",
          q: "'I'm ___ a message to my friend on WhatsApp.'",
          options: ["sending", "send", "am send"],
          a: "sending",
          successtip: "✓ WHATSAPP! Latino essential! 💬 WhatsApp = primary communication for immigrants! Free international messaging!"
        }
      ]
    },

    // SET 6: Mini-Readings - AI & Modern Tech
    {
      id: "m4-u4-set6",
      title: "Set 6: AI in Daily Life",
      questions: [
        {
          type: "mc",
          q: "Lee:\n\n'Roberto is using ChatGPT right now. He's asking questions about Canadian job interviews. The AI is giving him advice and it's helping him practice answers. He's also using it to improve his English writing. Technology is making his life easier!'\n\n¿Para qué está usando Roberto ChatGPT?",
          options: [
            "Para prepararse para entrevistas y mejorar su inglés",
            "Para ver videos",
            "Para jugar"
          ],
          a: "Para prepararse para entrevistas y mejorar su inglés",
          successtip: "✓ CHATGPT! AI assistant! 🤖 Using AI for job prep = smart immigrant move! Free career coach!"
        },
        {
          type: "input",
          q: "Complete: 'The AI ___ him advice.' (give - continuous)",
          a: "is giving"
        },
        {
          type: "match",
          q: "Match AI/Tech terms:",
          pairs: [
            { left: "Algorithm", right: "Sistema que decide qué ves" },
            { left: "AI-generated", right: "Creado por inteligencia artificial" },
            { left: "Screen time", right: "Tiempo frente a pantallas" }
          ],
          a: "completed",
          successtip: "✓ TECH LITERACY! Modern vocabulary! 💻 Algorithm = el feed que ves, AI = asistente digital, Screen time = hours on phone!"
        },
        {
          type: "mc",
          q: "Lee:\n\n'Sofia is tracking her screen time this week. She's spending 6 hours a day on her phone! She's trying to reduce it. She's turning off notifications and she's not checking social media before bed anymore.'\n\n¿Cuánto tiempo pasa Sofia en su teléfono al día?",
          options: ["6 hours", "2 hours", "8 hours"],
          a: "6 hours"
        },
        {
          type: "order",
          q: "Traduce: 'Estoy usando inteligencia artificial para aprender inglés'",
          words: ["I'm", "using", "artificial", "intelligence", "to", "learn", "English."],
          a: "I'm using artificial intelligence to learn English."
        },
        {
          type: "input",
          q: "Complete: 'She's turning ___ her notifications.' (off/on)",
          a: "off"
        },
        {
          type: "mc",
          q: "Lee:\n\n'David is using the Tim Hortons app. He's ordering his coffee before he arrives at the store. He's also earning points and he's getting free drinks! He's paying with Interac through the app. Canadian apps are making life convenient!'\n\n¿Qué está haciendo David con la app?",
          options: [
            "Ordering coffee and earning points",
            "Playing games",
            "Watching videos"
          ],
          a: "Ordering coffee and earning points",
          successtip: "✓ TIM HORTONS APP! Canadian essential! 🍩 Mobile order = skip the line! Interac = Canadian debit system!"
        },
        {
          type: "order",
          q: "Traduce: 'Estoy ganando puntos con mi aplicación de café'",
          words: ["I'm", "earning", "points", "with", "my", "coffee", "app."],
          a: "I'm earning points with my coffee app."
        },
        {
          type: "input",
          q: "Complete: 'He's ___ with Interac.' (pay - continuous)",
          a: "paying"
        },
        {
          type: "mc",
          q: "'I'm ___ my bank balance on my phone right now.'",
          options: ["checking", "check", "am check"],
          a: "checking"
        }
      ]
    },

    // SET 7: Phone Conversations
    {
      id: "m4-u4-set7",
      title: "Set 7: Phone Call Situations",
      questions: [
        {
          type: "mc",
          q: "Boss calls: 'Where are you? We have a meeting!' → 'Sorry, I ___ to work right now. Traffic is bad!'",
          options: ["am driving", "driving", "drive"],
          a: "am driving",
          tip: "Phone call = describe what you're doing RIGHT NOW = continuous!"
        },
        {
          type: "input",
          q: "Friend asks: 'What are you doing?' → 'I ___ TV.' (watch)",
          a: "am watching"
        },
        {
          type: "order",
          q: "Ordena (llamada urgente): 'Estoy ocupado ahora mismo, ¿puedo llamarte después?'",
          words: ["I'm", "busy", "right", "now,", "can", "I", "call", "you", "later?"],
          a: "I'm busy right now, can I call you later?"
        },
        {
          type: "mc",
          q: "Mom calls: '¿Qué estás haciendo, hijo?' → 'I ___ dinner, Mom!'",
          options: ["am cooking", "cooking", "cook"],
          a: "am cooking"
        },
        {
          type: "match",
          q: "Match phone situation to response:",
          pairs: [
            { left: "Can you talk?", right: "Sorry, I'm driving" },
            { left: "Where are you?", right: "I'm at the store" },
            { left: "What are you doing?", right: "I'm working right now" }
          ],
          a: "completed",
          successtip: "✓ PHONE ETIQUETTE! Canadian culture! 📱 Always explain what you're doing = polite way to say 'I'm busy'!"
        },
        {
          type: "input",
          q: "Coworker: 'Where are you?' → 'I ___ in the parking lot.' (wait)",
          a: "am waiting"
        },
        {
          type: "mc",
          q: "Family call home: 'Hola mama! I ___ you from work during my break.'",
          options: ["am calling", "calling", "call"],
          a: "am calling"
        },
        {
          type: "order",
          q: "Ordena: '¿Puedes oírme? ¿Estás ahí?'",
          words: ["Can", "you", "hear", "me?", "Are", "you", "there?"],
          a: "Can you hear me? Are you there?"
        },
        {
          type: "input",
          q: "Roommate: 'Where's the remote?' → 'I ___ it!' (use)",
          a: "am using"
        },
        {
          type: "mc",
          q: "Landlord calls: 'Why are you calling?' → 'The heater isn't working and I ___ in the apartment!'",
          options: ["am freezing", "freeze", "freezing"],
          a: "am freezing",
          successtip: "✓ TENANT RIGHTS! Canadian winter! ❄️ Heat not working = emergency! Landlord must fix within 24hrs!"
        }
      ]
    },

    // SET 8: Multitasking & While
    {
      id: "m4-u4-set8",
      title: "Set 8: Doing Two Things at Once",
      questions: [
        {
          type: "mc",
          q: "'I'm cooking dinner ___ listening to a podcast.'",
          options: ["while", "when", "because"],
          a: "while",
          tip: "'While' = mientras. Used for two actions happening at the same time! B1 level!"
        },
        {
          type: "input",
          q: "Complete: 'She's working ___ watching her kids.' (while)",
          a: "while"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy estudiando mientras escucho música'",
          words: ["I'm", "studying", "while", "listening", "to", "music."],
          a: "I'm studying while listening to music.",
          successtip: "✓ MULTITASKING! Immigrant reality! 💪 Two continuous actions = 'while listening' (no need for 'I am' twice!)"
        },
        {
          type: "mc",
          q: "'He's eating breakfast ___ reading the news on his phone.'",
          options: ["while", "because", "when"],
          a: "while"
        },
        {
          type: "match",
          q: "Match multitasking situation:",
          pairs: [
            { left: "Cooking while listening", right: "Kitchen + podcast" },
            { left: "Working while watching kids", right: "Remote work + parenting" },
            { left: "Eating while scrolling", right: "Meal + phone" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'I'm driving ___ talking on hands-free.' (while)",
          a: "while"
        },
        {
          type: "mc",
          q: "'We're watching TV ___ eating pizza.'",
          options: ["while", "because", "and"],
          a: "while"
        },
        {
          type: "order",
          q: "Ordena: 'Él está trabajando mientras toma café'",
          words: ["He's", "working", "while", "drinking", "coffee."],
          a: "He's working while drinking coffee."
        },
        {
          type: "input",
          q: "Complete: 'She's texting ___ walking.' (while - but dangerous!)",
          a: "while"
        },
        {
          type: "mc",
          q: "Real scenario: 'I'm learning English ___ working full-time and raising kids!'",
          options: ["while", "because", "when"],
          a: "while",
          successtip: "✓ SUPERHERO MODE! Immigrant hustle! 🦸 Work + study + family = you're amazing! Triple tasking = survival skill!"
        }
      ]
    },

    // SET 9: Workplace Tech Scenarios
    {
      id: "m4-u4-set9",
      title: "Set 9: Work & Technology",
      questions: [
        {
          type: "mc",
          q: "Zoom meeting: 'Sorry, I ___ you. Can you repeat?'",
          options: ["am not hearing", "not hearing", "don't hear"],
          a: "am not hearing",
          tip: "Right now problem = continuous! 'I'm not hearing you' = can't hear you NOW."
        },
        {
          type: "input",
          q: "Complete: 'Everyone ___ into the meeting.' (log in)",
          a: "is logging"
        },
        {
          type: "order",
          q: "Ordena: '¿Puedes ver mi pantalla?'",
          words: ["Can", "you", "see", "my", "screen?"],
          a: "Can you see my screen?"
        },
        {
          type: "mc",
          q: "'Hold on, I ___ my screen with the team.'",
          options: ["am sharing", "sharing", "share"],
          a: "am sharing",
          successtip: "✓ SCREEN SHARING! Remote work essential! 💻 'Share my screen' = mostrar tu pantalla. Zoom vocabulary!"
        },
        {
          type: "match",
          q: "Match Zoom problems to solutions:",
          pairs: [
            { left: "I'm not hearing you", right: "Check your speakers" },
            { left: "My camera isn't working", right: "Check permissions" },
            { left: "I'm lagging", right: "Check your internet" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'She ___ an email to the client.' (send - now)",
          a: "is sending"
        },
        {
          type: "mc",
          q: "Slack message: 'What are you working on?' → 'I ___ the monthly report.'",
          options: ["am finishing", "finish", "finishing"],
          a: "am finishing"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy en una reunión, te llamo después'",
          words: ["I'm", "in", "a", "meeting,", "I'll", "call", "you", "later."],
          a: "I'm in a meeting, I'll call you later."
        },
        {
          type: "input",
          q: "Complete: 'He ___ his presentation right now.' (prepare)",
          a: "is preparing"
        },
        {
          type: "mc",
          q: "Manager asks: 'Why aren't you responding to my messages?' → 'Sorry, I ___ with a customer!'",
          options: ["am dealing", "deal", "dealing"],
          a: "am dealing"
        }
      ]
    },

    // SET 10: Integration & Real Life
    {
      id: "m4-u4-set10",
      title: "Set 10: Real-Life Integration",
      questions: [
        {
          type: "mc",
          q: "ERROR mix: 'I ___ dinner while I ___ my mom on video call.' (cook, talk to)",
          options: ["am cooking, am talking to", "cook, talk to", "am cooking, talk to"],
          a: "am cooking, am talking to",
          tip: "Both happening NOW = both continuous! While = dos acciones simultáneas!"
        },
        {
          type: "input",
          q: "Complete question: 'What ___ you watching on Netflix right now?' (question word)",
          a: "are"
        },
        {
          type: "order",
          q: "Ordena: '¿Por qué estás usando tu teléfono durante la cena?'",
          words: ["Why", "are", "you", "using", "your", "phone", "during", "dinner?"],
          a: "Why are you using your phone during dinner?"
        },
        {
          type: "mc",
          q: "Video call home: 'Mira mama! Your grandson ___ his first words!'",
          options: ["is saying", "saying", "says"],
          a: "is saying",
          successtip: "✓ FAMILY MOMENTS! Video calls! 👨‍👩‍👧 Sharing milestones across borders = emotional immigrant reality!"
        },
        {
          type: "match",
          q: "Match situation to continuous question:",
          pairs: [
            { left: "Someone on phone", right: "Who are you texting?" },
            { left: "Someone cooking", right: "What are you making?" },
            { left: "Someone leaving", right: "Where are you going?" }
          ],
          a: "completed"
        },
        {
          type: "input",
          q: "Complete: 'She ___ for a new job while ___ at her current one.' (look, work)",
          a: "is looking, working"
        },
        {
          type: "mc",
          q: "'I'm ___ money this month ___ using a budgeting app.'",
          options: ["tracking, while", "track, because", "tracking, because"],
          a: "tracking, while"
        },
        {
          type: "order",
          q: "Ordena: 'Estamos aprendiendo inglés mientras trabajamos tiempo completo'",
          words: ["We're", "learning", "English", "while", "working", "full-time."],
          a: "We're learning English while working full-time."
        },
        {
          type: "input",
          q: "Phone scenario: 'I can't talk, I ___ the kids to school.' (drive)",
          a: "am driving"
        },
        {
          type: "mc",
          q: "Complete integration: 'What ___ you doing? Why ___ you calling me at midnight? I ___ sleeping!'",
          options: ["are, are, am", "do, do, am", "are, do, was"],
          a: "are, are, am",
          successtip: "✓ UNIT 4 COMPLETE! Question mastery! 🎯 You can now ask/answer about ongoing actions, use tech vocabulary, and describe phone situations perfectly! What ARE you doing? = MASTERED! 🎉📱"
        }
      ]
    }
  ],

// ===================================================================
// READING SET: Luis and Karen's Busy Day
// ===================================================================
readingSet: {
  id: "m4-u4-reading",
  title: "Set 11: Reading Practice",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Luis at the Coffee Shop (preguntas 1-3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Multitasking at Tim Hortons**

Hi! I'm Luis and right now I'm sitting at a coffee shop in downtown Toronto. I'm waiting for my friend Karen who's coming from Scarborough. 

While I'm waiting, I'm doing several things at once. I'm checking my work emails on my laptop because my boss is sending me urgent messages. I'm also listening to a podcast about Canadian immigration through my earbuds. And of course, I'm drinking my large Tim Hortons coffee!`
    },
    {
      type: "mc",
      q: "¿Qué está haciendo Luis en este momento?",
      options: [
        "Waiting for Karen at a coffee shop",
        "Working at his office",
        "Riding the TTC"
      ],
      a: "Waiting for Karen at a coffee shop",
      successtip: "✓ CURRENT ACTION!\nHe is at a coffee shop downtown. ☕"
    },
    {
      type: "mc",
      q: "¿Qué está haciendo Luis mientras espera?",
      options: [
        "Checking emails, listening to a podcast, and drinking coffee",
        "Only checking emails",
        "Sleeping"
      ],
      a: "Checking emails, listening to a podcast, and drinking coffee",
      successtip: "✓ MULTITASKING!\nHe is doing three things at the same time. 📧🎧"
    },
    {
      type: "mc",
      q: "¿De dónde viene Karen?",
      options: ["Scarborough", "Downtown", "Mississauga"],
      a: "Scarborough",
      successtip: "✓ LOCATION!\nShe is traveling from Scarborough to Downtown. 📍"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Karen's Situation (preguntas 4-5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Karen's Stressful Commute**

Karen just texted me - she's on the TTC right now and she's arriving in 10 minutes. She's not having a good day. She's dealing with a problem at work and her manager is calling her non-stop. She's also looking for a new apartment because her rent is increasing next month.`
    },
    {
      type: "mc",
      q: "¿Cómo está viajando Karen?",
      options: ["On the TTC (transit)", "By car", "Walking"],
      a: "On the TTC (transit)",
      successtip: "✓ TTC!\nToronto Transit Commission = subway, streetcar, or bus. 🚇"
    },
    {
      type: "mc",
      q: "¿Por qué Karen no está teniendo un buen día?",
      options: [
        "Problem at work and manager calling, plus rent increasing",
        "She's sick",
        "She lost her phone"
      ],
      a: "Problem at work and manager calling, plus rent increasing",
      successtip: "✓ STRESSFUL DAY!\nShe has both work and housing problems. 😰"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Improving Lives (preguntas 6-7)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Technology and Opportunities**

While we're waiting to meet, we're both using technology. I'm browsing job postings on Indeed because I'm looking for better opportunities. She's scrolling through Facebook Marketplace looking for cheap furniture. We're both trying to improve our lives here in Canada!`
    },
    {
      type: "mc",
      q: "¿Qué está buscando Luis en Indeed?",
      options: ["Job postings", "Apartments", "Furniture"],
      a: "Job postings",
      successtip: "✓ JOB HUNTING!\nLooking for better opportunities in Canada! 💼"
    },
    {
      type: "mc",
      q: "¿Qué está buscando Karen en Facebook Marketplace?",
      options: ["Cheap furniture", "A new phone", "A car"],
      a: "Cheap furniture",
      successtip: "✓ FACEBOOK MARKETPLACE!\nBest place for newcomers to find affordable furniture! 🛋️"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Grammar & Reflection (preguntas 8-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Language in Action**

The use of the **Present Continuous** (-ing) is very common when we talk about things happening right now or during a period of time. Using "while" helps us connect two actions that happen simultaneously.`
    },
    {
      type: "mc",
      q: "¿Cuántos verbos en presente continuo hay aproximadamente en el texto?",
      options: ["Más de 15", "Solo 5", "Ninguno"],
      a: "Más de 15",
      tip: "Sitting, waiting, checking, sending, drinking, arriving, calling... the text is full of -ING!",
      successtip: "✓ VERB WATCH!\nYou noticed how often we use -ing for current activities. 🎯"
    },
    {
      type: "mc",
      q: "En el texto, la palabra 'while' se utiliza para mostrar:",
      options: [
        "Acciones que ocurren al mismo tiempo",
        "Acciones en el pasado",
        "Acciones futuras"
      ],
      a: "Acciones que ocurren al mismo tiempo",
      successtip: "✓ WHILE!\nExcellent! 'While' = mientras. It connects simultaneous actions. ⏳"
    },
    {
      type: "mc",
      q: "Inferencia: ¿Por qué este texto representa bien la vida de muchos inmigrantes?",
      options: [
        "Muestra multitasking, búsqueda de mejores oportunidades, y problemas de renta",
        "Solo habla de café",
        "No menciona problemas"
      ],
      a: "Muestra multitasking, búsqueda de mejores oportunidades, y problemas de renta",
      successtip: "✓ REAL LIFE!\nMultiple job hunting, rent stress, and constant hustle is a shared experience. 💪"
    }
  ]
},
  // REVIEW SET
  reviewSet: {
    id: "m4-u4-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "mc",
        q: "Para hacer preguntas en presente continuo usas:",
        options: [
          "Question word + AM/IS/ARE + subject + verb-ING",
          "Question word + DO/DOES + subject + verb",
          "Subject + AM/IS/ARE + verb-ING"
        ],
        a: "Question word + AM/IS/ARE + subject + verb-ING"
      },
      {
        type: "match",
        q: "Match question word to meaning:",
        pairs: [
          { left: "What", right: "qué" },
          { left: "Where", right: "dónde" },
          { left: "Why", right: "por qué" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "'___ are you doing right now?'",
        options: ["What", "Where", "Who"],
        a: "What"
      },
      {
        type: "input",
        q: "Complete: 'I ___ to music.' (listen - continuous)",
        a: "am listening"
      },
      {
        type: "mc",
        q: "ERROR: 'I listening music' - ¿Qué faltan?",
        options: ["'am' y 'to'", "Solo 'to'", "Solo 'am'"],
        a: "'am' y 'to'",
        tip: "Dos errores: need AM (I am listening) y preposition TO (listening TO music)!"
      },
      {
        type: "order",
        q: "Ordena: '¿Qué estás haciendo ahora mismo?'",
        words: ["What", "are", "you", "doing", "right", "now?"],
        a: "What are you doing right now?"
      },
      {
        type: "mc",
        q: "'While' se usa para:",
        options: [
          "Dos acciones simultáneas",
          "Acciones pasadas",
          "Hacer preguntas"
        ],
        a: "Dos acciones simultáneas"
      },
      {
        type: "input",
        q: "Complete: 'She's cooking ___ listening to music.' (while)",
        a: "while"
      },
      {
        type: "mc",
        q: "Vocabulario tech: 'Scrolling' significa:",
        options: [
          "Navegar/deslizar por el feed",
          "Escribir",
          "Borrar"
        ],
        a: "Navegar/deslizar por el feed"
      },
      {
        type: "mc",
        q: "Complete integration: 'What ___ you doing? I ___ you three times! Where ___ you going?'",
        options: ["are, am calling, are", "do, call, do", "are, call, are"],
        a: "are, am calling, are",
        successtip: "✓ UNIT 4 MASTERED! 🎉 You can now ask detailed questions about ongoing actions, use tech vocabulary naturally, and describe complex situations with 'while'! Phone conversations = NO PROBLEM! 📱💪🇨🇦"
      }
    ]
  }
};
